/**
 * <CookieBanner />
 *
 * Bottom-docked compliance banner that appears until the user dismisses.
 * State persisted in localStorage under `pb-cookie-consent` ("accepted"
 * or "declined"). No 3rd-party cookies, no tracking -- the banner is
 * informational and keeps the site GDPR-polite.
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "../ui/Button";

const STORAGE_KEY = "pb-cookie-consent";

export function CookieBanner() {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const persist = (value: "accepted" | "declined") => {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* storage unavailable -- banner just re-renders next load */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie notice"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-50 p-3 md:p-5"
    >
      <div className="mx-auto max-w-5xl rounded-2xl border border-surface-border bg-white shadow-card-lg px-5 py-4 flex flex-col md:flex-row md:items-center gap-4">
        <Cookie className="h-7 w-7 text-brand flex-shrink-0" aria-hidden />
        <div className="flex-1 min-w-0 text-sm text-ink">
          <p className="font-semibold">We use essential cookies only.</p>
          <p className="text-ink-muted mt-0.5">
            Parts Buoy keeps a login session cookie and a Stripe checkout
            cookie — nothing else. See our{" "}
            <Link
              to="/privacy"
              className="underline font-semibold text-ink hover:text-brand"
            >
              Privacy policy
            </Link>{" "}
            for details.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => persist("declined")}
            data-testid="cookie-banner-decline"
          >
            Decline
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => persist("accepted")}
            data-testid="cookie-banner-accept"
          >
            Accept
          </Button>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => persist("declined")}
            data-testid="cookie-banner-dismiss"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-muted hover:text-ink"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
